import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types/user';

interface AuthState {
  // User auth state
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;

  // Admin auth state
  adminToken: string | null;
  isAdminAuthenticated: boolean;

  // UI state
  isLoading: boolean;
  isDarkMode: boolean;
  hasHydrated: boolean;

  // User actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setCredentials: (user: User, token: string) => void;
  logout: () => void;

  // Admin actions
  setAdminToken: (token: string | null) => void;
  adminLogout: () => void;

  // UI actions
  setLoading: (loading: boolean) => void;
  toggleDarkMode: () => void;
  setDarkMode: (isDark: boolean) => void;
  initializeTheme: () => void;
  setHasHydrated: (hasHydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      adminToken: null,
      isAdminAuthenticated: false,
      isLoading: false,
      isDarkMode: false,
      hasHydrated: false,

      setHasHydrated: (hasHydrated) => set({ hasHydrated }),

      // User actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setToken: (token) =>
        set({
          token,
          isAuthenticated: !!token,
        }),

      setCredentials: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        }),

      // Admin actions
      setAdminToken: (adminToken) =>
        set({
          adminToken,
          isAdminAuthenticated: !!adminToken,
        }),

      adminLogout: () =>
        set({
          adminToken: null,
          isAdminAuthenticated: false,
        }),

      // UI actions
      setLoading: (isLoading) => set({ isLoading }),

      toggleDarkMode: () => {
        const newDarkMode = !get().isDarkMode;
        set({ isDarkMode: newDarkMode });
        if (newDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      setDarkMode: (isDark) => {
        set({ isDarkMode: isDark });
        if (isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      initializeTheme: () => {
        const { isDarkMode } = get();
        // Check system preference if not already set
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const shouldBeDark = isDarkMode || prefersDark;

        if (shouldBeDark) {
          document.documentElement.classList.add('dark');
          set({ isDarkMode: true });
        } else {
          document.documentElement.classList.remove('dark');
        }
      },
    }),
    {
      name: 'keepnota-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        adminToken: state.adminToken,
        isAdminAuthenticated: state.isAdminAuthenticated,
        isDarkMode: state.isDarkMode,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

export default useAuthStore;
